import { QUOTATION_API_URL } from "../../../../config";
import { LogCategory } from "../../../../config/logging";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { buildQuotationApiHeaders } from "./quotationApiHeaders";

const RELEVANT_PAYMENT_METHOD_IDS = new Set([5, 6, 7, 8, 9, 10, 12, 13]);
const DEFERRED_PAYMENT_METHOD_IDS = new Set([8, 9, 12]);

export interface PaymentMethodOption {
	id: number;
	descricao: string;
	status: number;
	permiteParcelamento: boolean;
	maxParcelas: number | null;
	requerAprovacao: boolean;
	percentual: number | null;
}

export interface FinalizeQuotationOrderInput {
	orcId: number;
	userId: number;
	clientName: string;
	formaPagamentoId: number | null;
	exibirRecibo: boolean;
	precisaNotaFiscal: boolean;
	temTransportadora: boolean;
	observacaoPedido: string;
}

export interface FinalizeQuotationOrderResult {
	raw: unknown;
}

export interface ProcessFinancialApprovalInput {
	orcId: number;
	userId: number;
	totalValue: number;
	clientId: number;
	clientName: string;
	paymentMethod: PaymentMethodOption;
}

export interface ProcessFinancialApprovalResult {
	statusOrcamento: string | null;
	statusPagamento: string | null;
	formaPagamentoId: number | null;
	raw: any;
}

interface RawPaymentMethod {
	fpg_id?: number;
	fpg_descricao?: string | null;
	fpg_status?: number | null;
	permite_parcelamento?: boolean | number | null;
	max_parcelas?: number | null;
	requer_aprovacao?: boolean | number | null;
	fpg_percentual?: number | string | null;
}

function getQuotationApiBaseUrl(): string {
	const base = String(QUOTATION_API_URL || "").trim();
	if (!base || !base.startsWith("http")) {
		throw new Error(
			"Base da API de orcamento invalida para fechamento financeiro.",
		);
	}
	return base.endsWith("/") ? base.slice(0, -1) : base;
}

async function parseJsonSafe(response: Response): Promise<any> {
	try {
		return await response.json();
	} catch {
		return {};
	}
}

function parseErrorMessage(data: any, fallback: string): string {
	return (
		data?.message ||
		data?.error ||
		data?.msg ||
		data?.data?.message ||
		data?.data?.error ||
		fallback
	);
}

function normalizeBoolean(value: boolean | number | null | undefined): boolean {
	if (typeof value === "boolean") return value;
	return value === 1;
}

function normalizeNumber(value: number | string | null | undefined): number | null {
	if (value === null || value === undefined || value === "") return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}

export function isDeferredPaymentMethodId(paymentMethodId: number): boolean {
	return DEFERRED_PAYMENT_METHOD_IDS.has(paymentMethodId);
}

export function mapPaymentMethodFromApi(
	method: RawPaymentMethod,
): PaymentMethodOption | null {
	const id = Number(method?.fpg_id);
	if (!Number.isFinite(id) || id <= 0) {
		return null;
	}

	const descricao = String(method?.fpg_descricao || "").trim();
	if (!descricao) {
		return null;
	}

	return {
		id,
		descricao,
		status: Number(method?.fpg_status ?? 0),
		permiteParcelamento: normalizeBoolean(method?.permite_parcelamento),
		maxParcelas: normalizeNumber(method?.max_parcelas),
		requerAprovacao: normalizeBoolean(method?.requer_aprovacao),
		percentual: normalizeNumber(method?.fpg_percentual),
	};
}

export function isRelevantPaymentMethod(
	method: PaymentMethodOption,
): boolean {
	return method.status === 1 && RELEVANT_PAYMENT_METHOD_IDS.has(method.id);
}

export async function listQuotationPaymentMethods(): Promise<
	PaymentMethodOption[]
> {
	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/formas-pagamento`;
	const headers = buildQuotationApiHeaders();

	sendToTerminal(
		"🔎 [QUOTATION_PAYMENT:LOAD_METHODS] Carregando formas de pagamento",
		{
			endpoint,
			tenantId: headers["X-Tenant-Id"] || "(nao enviado)",
			appRegion: headers["x-app-region"] || "(nao enviada)",
		},
		LogCategory.API,
	);

	const response = await fetch(endpoint, {
		method: "GET",
		headers,
		mode: "cors",
	});

	const data = await parseJsonSafe(response);
	if (!response.ok) {
		throw new Error(
			parseErrorMessage(data, "Erro ao carregar formas de pagamento."),
		);
	}

	const methods = Array.isArray(data)
		? data
				.map((item) => mapPaymentMethodFromApi(item))
				.filter((item): item is PaymentMethodOption => item !== null)
				.filter(isRelevantPaymentMethod)
		: [];

	sendToTerminal(
		"✅ [QUOTATION_PAYMENT:LOAD_METHODS] Formas de pagamento carregadas",
		{
			total: methods.length,
			methodIds: methods.map((method) => method.id),
		},
		LogCategory.BUSINESS,
	);

	return methods;
}

// Status de pagamento inicial ao fechar o pedido: aguardando.
// PIX/financeiro atualizam o status depois.
const PAYMENT_STATUS_AGUARDANDO = 1;

/**
 * Finaliza o pedido (fechamento) via PUT /updateOrcamentoFinal/:id.
 *
 * Persiste forma de pagamento, recibo, nota fiscal, transportadora e observação
 * do pedido, avança o status para "pedido" (ost_id=2) e dispara as notificações
 * de financeiro (NF) e expedição (transportadora). Reutiliza a rota existente —
 * sem mudança de backend.
 */
export async function finalizeQuotationOrder(
	input: FinalizeQuotationOrderInput,
): Promise<FinalizeQuotationOrderResult> {
	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/updateOrcamentoFinal/${input.orcId}`;
	const headers = buildQuotationApiHeaders();

	const payload = {
		orc_id: input.orcId,
		client_name: input.clientName,
		exibirRecibo: input.exibirRecibo,
		observacao: input.observacaoPedido?.trim() || null,
		user_usuultalteracao: input.userId,
		...(input.formaPagamentoId
			? { formaPagamento: input.formaPagamentoId }
			: {}),
		id_pg_status: PAYMENT_STATUS_AGUARDANDO,
		checkTransportadora: input.temTransportadora,
		checkNotaFiscal: input.precisaNotaFiscal,
	};

	sendToTerminal(
		"🚀 [QUOTATION_CLOSING:FINALIZE] Salvando fechamento do pedido",
		{
			endpoint,
			orcId: input.orcId,
			formaPagamentoId: input.formaPagamentoId,
			temTransportadora: input.temTransportadora,
			precisaNotaFiscal: input.precisaNotaFiscal,
			exibirRecibo: input.exibirRecibo,
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "PUT",
		headers,
		body: JSON.stringify(payload),
		mode: "cors",
	});

	const data = await parseJsonSafe(response);
	if (!response.ok) {
		throw new Error(
			parseErrorMessage(data, "Erro ao salvar fechamento do pedido."),
		);
	}

	sendToTerminal(
		"✅ [QUOTATION_CLOSING:FINALIZE] Fechamento do pedido salvo",
		{ orcId: input.orcId },
		LogCategory.BUSINESS,
	);

	return { raw: data };
}

export async function processQuotationFinancialApproval(
	input: ProcessFinancialApprovalInput,
): Promise<ProcessFinancialApprovalResult> {
	const apiBaseUrl = getQuotationApiBaseUrl();
	const endpoint = `${apiBaseUrl}/processar-pagamento/${input.orcId}`;
	const headers = buildQuotationApiHeaders();
	const totalValue = Number(input.totalValue.toFixed(2));
	const isDeferredPayment =
		input.paymentMethod.requerAprovacao ||
		isDeferredPaymentMethodId(input.paymentMethod.id);

	const payload = {
		orc_id: input.orcId,
		valor_pagamento: totalValue,
		is_pagamento_parcial: false,
		is_deferred_payment: isDeferredPayment,
		orc_valor_total: totalValue,
		ref_pay_value_total: 1,
		user_usucadastro: input.userId,
		forma_pagamento_id: input.paymentMethod.id,
		cliente: {
			cli_id: input.clientId,
			cli_nome: input.clientName,
			carteira_one1_cliente: null,
		},
	};

	sendToTerminal(
		"🚀 [QUOTATION_PAYMENT:PROCESS_FINANCIAL] Processando fechamento financeiro",
		{
			endpoint,
			orcId: input.orcId,
			clientId: input.clientId,
			paymentMethodId: input.paymentMethod.id,
			paymentMethod: input.paymentMethod.descricao,
			isDeferredPayment,
			totalValue,
		},
		LogCategory.BUSINESS,
	);

	const response = await fetch(endpoint, {
		method: "PUT",
		headers,
		body: JSON.stringify(payload),
		mode: "cors",
	});

	const data = await parseJsonSafe(response);
	if (!response.ok) {
		throw new Error(
			parseErrorMessage(data, "Erro ao processar aprovacao financeira."),
		);
	}

	sendToTerminal(
		"✅ [QUOTATION_PAYMENT:PROCESS_FINANCIAL] Fechamento financeiro concluido",
		{
			orcId: input.orcId,
			statusOrcamento: data?.status_orcamento ?? null,
			statusPagamento: data?.status_pagamento ?? null,
			formaPagamentoId: data?.forma_pagamento_id ?? null,
		},
		LogCategory.BUSINESS,
	);

	return {
		statusOrcamento: data?.status_orcamento ?? null,
		statusPagamento: data?.status_pagamento ?? null,
		formaPagamentoId: Number(data?.forma_pagamento_id ?? input.paymentMethod.id),
		raw: data,
	};
}
